import { useState } from 'react';
import { can } from '@kia/shared';
import { useAuth } from '../auth/AuthProvider';
import { Sidebar } from '../nav/Sidebar';
import { landingFor, type View } from '../nav/navModel';
import { useHashRoute } from '../nav/useHashRoute';
import { jumpTarget, type FocusKind } from '../nav/jumpTarget';
import { AiEntryChat } from './AiEntryChat';
import { AnalysisScreen } from './AnalysisScreen';
import { AuditScreen } from './AuditScreen';
import { BiScreen } from './BiScreen';
import { BuyScreen } from './BuyScreen';
import { CashCalendarScreen } from './CashCalendarScreen';
import { AccountingScreen } from './AccountingScreen';
import { CloseScreen } from './CloseScreen';
import { CustomersScreen } from './CustomersScreen';
import { DashboardScreen } from './DashboardScreen';
import { DealsScreen } from './DealsScreen';
import { type SearchType } from './GlobalSearch';
import { HelpScreen } from './HelpScreen';
import { InquiriesScreen, type DealPrefill } from './InquiriesScreen';
import { MasterDataScreen } from './MasterDataScreen';
import { MotorDesignerScreen } from './MotorDesignerScreen';
import { OnboardingGate } from './OnboardingGate';
import { PricesScreen } from './PricesScreen';
import { SenseCheckScreen } from './SenseCheckScreen';
import { ReleasesScreen } from './ReleasesScreen';
import { SettingsScreen } from './SettingsScreen';
import { SmartEntryDock } from './SmartEntryDock';
import { UsersScreen } from './UsersScreen';

export function AppShell() {
  const { user } = useAuth();
  // The current view lives in the URL hash (CW-11) so a refresh keeps the user where they were, Back/Forward
  // work, and a screen is shareable — `setView` is the router's `navigate`, so every existing setView(x) call
  // now drives the URL. The parse fallback is the role's daily workspace (landingFor): Warehouse/Security open
  // on the dispatch queue, Finance on the cash calendar, Sales on the pipeline. RBAC-guarded.
  const { view, navigate: setView } = useHashRoute(user ? landingFor(user.role) : 'dash');
  // A convert action in the inbox seeds the next deal form (consumed on mount).
  const [dealPrefill, setDealPrefill] = useState<DealPrefill | null>(null);
  // A global-search hit deep-links a specific record open (deal / release / help article).
  const [focusDeal, setFocusDeal] = useState<number | null>(null);
  const [focusCustomer, setFocusCustomer] = useState<number | null>(null);
  const [focusRelease, setFocusRelease] = useState<number | null>(null);
  const [focusHelp, setFocusHelp] = useState<number | null>(null);
  // «صدور فاکتور مالیاتی» on a deal deep-links to the ledger's tax-invoice panel, prefilled (E13-10).
  const [focusInvoiceDeal, setFocusInvoiceDeal] = useState<number | null>(null);
  // «آماده‌سازی حواله» on a deal deep-links to the ترخیص screen with PrepareCard pre-seeded to it (FLOW-1) —
  // a deal-seed channel mirroring focusInvoiceDeal, NOT applyFocus('release') (which addresses release ROWS).
  const [focusReleaseDeal, setFocusReleaseDeal] = useState<number | null>(null);
  // «تحلیل با دستیار» on a deal deep-links to «دستیار تحلیل» with the composer pre-seeded with a
  // natural-language question about that deal (AI-3) — a seed channel mirroring focusInvoiceDeal, carrying
  // model-visible TEXT (the deal's business no, never its internal id). Consumed (nulled) once filled.
  const [aiSeed, setAiSeed] = useState<string | null>(null);
  // The onboarding CTAs deep-link to a specific SUB-TAB of the tab-stickied Settings/Prices screens, so a
  // fix like «فرض ماهانه» opens the cost tab, not the last-used one. Consumed (nulled) once applied.
  const [focusSettingsTab, setFocusSettingsTab] = useState<string | null>(null);
  const [focusPricesTab, setFocusPricesTab] = useState<string | null>(null);
  if (!user) return null;

  // The ONE deep-link applier: navigate to `view` and set the named focus state / sub-tab. Both a global-search
  // hit (jumpTarget) and a notification-bell click (resolveNotificationTarget) resolve to the SAME
  // {view, focus, focusId, focusTab} contract and land through here, so search and the bell behave identically
  // and there's one place the focus states are set (CW-10). Never drops the id onto a screen the role can't open.
  const applyFocus = (view: View, focus?: FocusKind, focusId?: number, focusTab?: string) => {
    setView(view);
    if (focus === 'deal' && focusId != null) setFocusDeal(focusId);
    else if (focus === 'release' && focusId != null) setFocusRelease(focusId);
    else if (focus === 'help' && focusId != null) setFocusHelp(focusId);
    else if (focus === 'customer' && focusId != null) setFocusCustomer(focusId);
    if (focusTab) {
      if (view === 'settings') setFocusSettingsTab(focusTab);
      else if (view === 'prices') setFocusPricesTab(focusTab);
    }
  };
  const handleJump = (type: SearchType, id: number) => {
    const t = jumpTarget(type, user.role);
    if (t) applyFocus(t.view, t.focus, id);
  };

  return (
    <div className="applayout">
      <Sidebar
        view={view}
        onNavigate={setView}
        onJump={handleJump}
        onNotify={(t) => applyFocus(t.view, t.focus, t.focusId, t.focusTab)}
      />

      <main className="appmain">
        {view === 'dash' ? (
          <DashboardScreen
            onOpenDeal={(id) => {
              setFocusDeal(id);
              setView('deals');
            }}
            onOpenInquiries={() => setView('inbox')}
            onOpenPrices={() => setView('prices')}
            onNavigate={(v, focusDealId) => {
              setView(v);
              if (focusDealId != null) setFocusDeal(focusDealId);
            }}
            onOpenCustomer={(id) => {
              setFocusCustomer(id);
              setView('customers');
            }}
          />
        ) : view === 'deals' ? (
          <DealsScreen
            prefill={dealPrefill}
            onPrefillConsumed={() => setDealPrefill(null)}
            openDealId={focusDeal}
            onDealOpened={() => setFocusDeal(null)}
            onIssueInvoice={
              can(user.role, 'accounting.post')
                ? (id) => {
                    setFocusInvoiceDeal(id);
                    setView('ledger');
                  }
                : undefined
            }
            onPrepareRelease={
              can(user.role, 'release.prepare')
                ? (id) => {
                    setFocusReleaseDeal(id);
                    setView('release');
                  }
                : undefined
            }
            onOpenAnalyst={
              can(user.role, 'ai.use')
                ? (seed) => {
                    setAiSeed(seed);
                    setView('ai');
                  }
                : undefined
            }
            onOpenDeal={(id) => {
              setFocusDeal(id);
              setView('deals');
            }}
            onOpenCustomer={(id) => {
              setFocusCustomer(id);
              setView('customers');
            }}
            onNavigate={(v, focusDealId) => {
              setView(v);
              if (focusDealId != null) setFocusDeal(focusDealId);
            }}
          />
        ) : view === 'inbox' ? (
          <InquiriesScreen
            onConvert={(p) => {
              setDealPrefill(p);
              setView('deals');
            }}
          />
        ) : view === 'customers' ? (
          <CustomersScreen
            openCustomerId={focusCustomer}
            onCustomerOpened={() => setFocusCustomer(null)}
          />
        ) : view === 'prices' ? (
          <PricesScreen focusTab={focusPricesTab} onTabFocused={() => setFocusPricesTab(null)} />
        ) : view === 'sense' ? (
          <SenseCheckScreen />
        ) : view === 'close' ? (
          <CloseScreen />
        ) : view === 'cash' ? (
          <CashCalendarScreen
            onOpenDeal={(id) => {
              setFocusDeal(id);
              setView('deals');
            }}
            onOpenCustomer={(id) => {
              setFocusCustomer(id);
              setView('customers');
            }}
            onNavigate={(v, focusDealId) => {
              setView(v);
              if (focusDealId != null) setFocusDeal(focusDealId);
            }}
          />
        ) : view === 'buy' ? (
          <BuyScreen />
        ) : view === 'release' ? (
          <ReleasesScreen
            focusRelease={focusRelease}
            onFocused={() => setFocusRelease(null)}
            focusReleaseDeal={focusReleaseDeal}
            onPrepareDealFocused={() => setFocusReleaseDeal(null)}
          />
        ) : view === 'ledger' ? (
          <AccountingScreen
            focusInvoiceDeal={focusInvoiceDeal}
            onInvoiceFocused={() => setFocusInvoiceDeal(null)}
          />
        ) : view === 'motor' ? (
          <MotorDesignerScreen />
        ) : view === 'master' ? (
          <MasterDataScreen />
        ) : view === 'bi' ? (
          <BiScreen />
        ) : view === 'ai' ? (
          <AnalysisScreen
            onOpenDeal={(id) => {
              setFocusDeal(id);
              setView('deals');
            }}
            seed={aiSeed ?? undefined}
            onSeedConsumed={() => setAiSeed(null)}
          />
        ) : view === 'entry' ? (
          <AiEntryChat />
        ) : view === 'help' ? (
          <HelpScreen role={user.role} focusHelp={focusHelp} onAskAi={() => setView('ai')} />
        ) : view === 'users' ? (
          <UsersScreen />
        ) : view === 'audit' ? (
          <AuditScreen />
        ) : (
          <SettingsScreen
            focusTab={focusSettingsTab}
            onTabFocused={() => setFocusSettingsTab(null)}
          />
        )}
      </main>

      {/* Floating smart-entry launcher — reachable from every screen (not the
          dedicated «ثبت هوشمند» tab itself, where it would be redundant). */}
      {can(user.role, 'ai.use') && view !== 'entry' ? <SmartEntryDock /> : null}

      {/* Proactive setup gate — Admin-only; guides the owner through the inputs that make COGS/price/margin
          real, deep-linking each fix. Renders nothing once fully configured. */}
      <OnboardingGate
        onNavigate={(v, tab) => {
          setView(v);
          if (tab && v === 'settings') setFocusSettingsTab(tab);
          else if (tab && v === 'prices') setFocusPricesTab(tab);
        }}
      />
    </div>
  );
}
